import {action, computed, makeObservable, observable} from 'mobx';

import type {Destructible} from './types';

import {AppError} from '../errors/app';

export interface WithBusyManager<ReasonKey extends string> {
  readonly busy: AsyncBusyManager<ReasonKey>;
}

type AnnotationsMap<
  T,
  AdditionalFields extends PropertyKey,
  ReasonKey extends string,
> = {
  [P in Exclude<keyof T, 'toString'>]?: ReasonKey;
} & Record<AdditionalFields, ReasonKey>;

type NoInfer<T> = [T][T extends any ? 0 : never];

export class AsyncBusyManager<ReasonKey extends string>
  implements Destructible
{
  private flags: Map<ReasonKey, boolean>;
  private isDestructed = false;

  constructor() {
    this.flags = new Map<ReasonKey, boolean>();
    makeObservable<AsyncBusyManager<ReasonKey>, 'flags'>(this, {
      anyReason: computed,
      flags: observable,
      register: action.bound,
      setReason: action.bound,
    });
  }

  destruct() {
    this.isDestructed = true;
    this.flags.clear();
  }

  get anyReason() {
    return Array.from(this.flags.values()).some(Boolean);
  }

  getReason(reason: ReasonKey) {
    if (this.flags.has(reason)) {
      return this.flags.get(reason);
    } else {
      throw new AppError(`Reason ${reason} is not registered`);
    }
  }

  observe<T extends object, AdditionalKeys extends PropertyKey = never>(
    target: T,
    annotations?: AnnotationsMap<T, NoInfer<AdditionalKeys>, ReasonKey>,
  ) {
    Object.entries<ReasonKey>(annotations ?? {}).forEach(
      ([methodName, reason]) => {
        /**
         * As TS doesn't warn about non-existing private methods in the target object,
         * we need to check it manually
         */
        if (methodName in target) {
          this.register(reason);

          target[methodName] = this.proxify(target[methodName], reason);
        } else {
          throw new AppError(
            `Trying to observe non-existing method ${methodName} of ${target.constructor.name}`,
          );
        }
      },
    );

    return this;
  }

  register(reason: ReasonKey | ReasonKey[]) {
    const reasons = Array.isArray(reason) ? reason : [reason];

    reasons.forEach(this.registerIfNotExists.bind(this));

    return this;
  }

  setReason(reason: ReasonKey, value: boolean) {
    if (this.flags.has(reason)) {
      this.flags.set(reason, value);
    } else if (!this.isDestructed) {
      /**
       * After destruct an async operation started before it may still settle
       * and reset its reason, so we ignore it instead of throwing
       */
      throw new AppError(
        `You are trying to set reason ${reason}, but you have to register it first`,
      );
    }

    return this;
  }

  private proxify(method, reason: ReasonKey) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return new Proxy(method, {
      async apply(target, thisArg, args) {
        self.setReason(reason, true);

        try {
          return await target.apply(thisArg, args);
        } finally {
          self.setReason(reason, false);
        }
      },
    });
  }

  private registerIfNotExists(reason: ReasonKey) {
    if (!this.flags.has(reason)) {
      this.flags.set(reason, false);
    }
  }
}
